/** Authoritative pure Daaluu engine. User-confirmed rules: docs/DAALUU_RULES.md. */
import type { Rng } from './cards.js';
import type { GameState, ViewExtras } from './game.js';
import type { BotDifficulty, GameView } from './protocol.js';
import { createDaaluuShuffle, daaluuShuffleViewFor, type DaaluuShuffleState, type DaaluuShuffleView } from './daaluuShuffle.js';
import { daaluuFreedomRequirements, daaluuMasterOrder, daaluuRequiredHouses, ultimateDaaluuMaster } from './daaluuSlaves.js';
import { beginDaaluuTributeBatchGive, beginDaaluuTributeBatchRelayReturn, completeDaaluuTributeBatchMaster, completeDaaluuTributeBatchRelay } from './daaluuTributeBatch.js';
import { DAALUU_KINDS, daaluuKind, type DaaluuKind, type DaaluuTile } from './daaluuKinds.js';
import { power, daaluuTributeTiles, daaluuRequiredTributeTiles, daaluuBatchRelayTiles, isDaaluuAravguiHand,
  legalDaaluuAravguiContributions, validDaaluuTributeReturn, legalDaaluuPairContributions } from './daaluuTileChoices.js';
export { daaluuRequiredHouses } from './daaluuSlaves.js';
export { DAALUU_KINDS, daaluuKind };
export type { DaaluuKind, DaaluuTile };
export { daaluuTributeTiles, daaluuRequiredTributeTiles, daaluuBatchRelayTiles, isDaaluuAravguiHand,
  legalDaaluuAravguiContributions, validDaaluuTributeReturn, legalDaaluuPairContributions };

export interface DaaluuLedger { tea: number[]; debt: number[][]; masters: (number | null)[]; slaveOrder: number[] }
const DAALUU_FREEDOM_HOUSES = 2;
export interface DaaluuPlay { seat: number; tiles: DaaluuTile[]; deadTileIds?: string[] }
/** Public, completed houses; bottom-to-top order, with the winning tile last. */
export interface DaaluuCapturePile { tiles: DaaluuTile[]; deadTileIds?: string[]; /** Stable position in the owner's capture tray. */ slot?: number }
export interface DaaluuHouseTransfer { pileId: string; fromSeat: number; toSeat: number }
export interface DaaluuDistribution {
  startIndex: number | null;
  collected: number;
  lastCollection: { index: number; seat: number; serial: number } | null;
}
export interface DaaluuTribute {
  slaveSeat: number; masterSeat: number; offered: DaaluuTile[]; returned: DaaluuTile[];
  relay?: { masterSeat: number; offered: DaaluuTile[]; returned: DaaluuTile[] };
}
export interface DaaluuBailoutLoan { buyerSeat: number; debtorSeat: number; lenderSeat: number; amount: number }
export interface DaaluuBailoutOffer { buyerSeat: number; debtorSeat: number; lenderSeat?: number; loanAmount?: number }
export interface DaaluuTributeBatch {
  masterSeat: number; firstSlaveSeat: number; slaveSeats: number[];
  phase: 'giving' | 'master-return' | 'relay-give' | 'relay-return'; giveIndex: number; relayIndex: number;
  offers: { slaveSeat: number; offered: DaaluuTile[]; big: DaaluuTile[]; middle: DaaluuTile[]; returned: DaaluuTile[] }[];
  masterReturns: DaaluuTile[];
}
export interface DaaluuAravgui {
  holderSeat: number; plays: DaaluuPlay[];
}
/** Open settlement: surplus holders race to fill the remaining house deficits. */
export interface DaaluuDebtChoice {
  remaining: number[]; surplus: number[]; shortOwnHouses: number[];
}
export interface DaaluuMovement {
  tiles: DaaluuTile[]; fromSeat: number; toSeat: number;
  from: 'hand' | 'board' | 'capture'; to: 'board' | 'hand' | 'capture'; phase: 0 | 1 | 2;
}
export interface DaaluuMotion { serial: number; movements: DaaluuMovement[] }
export const DAALUU_FLIGHT_MS = 650;
export const DAALUU_HOUSE_FLIGHT_MS = 1400;
export const DAALUU_PLAY_MS = 1600;
export const DAALUU_JANLII_PICK_MS = 1500;
export const DAALUU_JANLII_ANNOUNCEMENT_MS = 2500;
export const DAALUU_JANLII_REVEAL_MS = DAALUU_JANLII_PICK_MS + DAALUU_JANLII_ANNOUNCEMENT_MS;
/** Shared presentation budget; only the server owns the corresponding timers. */
export function daaluuMotionTiming(d: Pick<DaaluuState, 'motion' | 'trick' | 'lastTrick'>) {
  const plays = [...d.trick, ...(d.lastTrick?.plays ?? [])];
  const reveals = d.motion.movements.some(move => move.to === 'board' && move.tiles.some(tile => plays.some(play => play.tiles.some(played => played.id === tile.id))));
  const placementMs = reveals ? DAALUU_PLAY_MS : DAALUU_FLIGHT_MS;
  const phaseMs = placementMs + (d.motion.movements.some(move => move.to === 'capture') ? 400 : 0);
  return { placementMs, phaseMs, durationMs: Math.max(0, ...d.motion.movements.map(move =>
    (move.phase === 2 ? phaseMs + DAALUU_FLIGHT_MS : move.phase * phaseMs) +
    (move.from === 'capture' && move.to === 'capture' ? DAALUU_HOUSE_FLIGHT_MS
      : move.to === 'board' && reveals ? DAALUU_PLAY_MS : DAALUU_FLIGHT_MS))) };
}
export interface DaaluuState {
  stage: 'mixing' | 'retry' | 'calling' | 'selecting' | 'distributing' | 'arranging' | 'pair-call' | 'pair-response' | 'tribute-reveal' | 'tribute-relay-reveal' | 'bailout' | 'bailout-choice' | 'tribute-give' | 'tribute-return' | 'aravgui-call' | 'aravgui-response' | 'debt-choice' | 'playing' | 'round-over' | 'finished';
  round: number; callerSeat: number; janlii: string | null; shuffle: DaaluuShuffleState;
  hands: DaaluuTile[][]; reserve: DaaluuTile[]; inventory: DaaluuTile[];
  ledger: DaaluuLedger; captured: DaaluuTile[][]; houses: number[]; earnedHouses: number[]; quota: number;
  capturePiles: DaaluuCapturePile[][];
  houseTransfers: DaaluuHouseTransfer[];
  lowTrick: boolean;
  /** Remaining seats to show, one at a time, before the chosen leader acts. */
  leaderSkips: { serial: number; seats: number[] } | null;
  distribution: DaaluuDistribution;
  pairCollection: { holderSeat: number; color: DaaluuKind['color'] } | null;
  /** Seat originally dealt both Janlii tiles. The color call happens after tribute. */
  originalPairHolder: number | null;
  tribute: DaaluuTribute | null; processedTributes: number[]; motion: DaaluuMotion;
  tributeBatch: DaaluuTributeBatch | null;
  /** Room rule. When false, replies need not follow the led color (pair-of-led-color and death rules still apply). Missing = true. */
  followColor?: boolean;
  aravgui: DaaluuAravgui | null; aravguiSeats: number[]; processedAravguiSeats: number[];
  debtChoice: DaaluuDebtChoice | null;
  trick: DaaluuPlay[]; winningSeat: number | null; lastTrick: { winnerSeat: number; plays: DaaluuPlay[] } | null;
  bailoutSeats: number[]; processedBailoutSeats: number[]; processedBailoutDebtors: number[];
  bailoutOffers: DaaluuBailoutOffer[]; bailoutLoans: DaaluuBailoutLoan[]; dead: string[][];
}
export interface DaaluuView {
  tribute: DaaluuTribute | null; aravgui: DaaluuAravgui | null; motion: DaaluuMotion;
  tributeBatch?: DaaluuTributeBatch | null;
  pairCollection: DaaluuState['pairCollection'];
  stage: DaaluuState['stage']; round: number; callerSeat: number; janlii: string | null;
  shuffle: DaaluuShuffleView; hand: DaaluuTile[]; trick: DaaluuPlay[];
  lastTrick: DaaluuState['lastTrick']; houses: number[]; earnedHouses: number[]; quota: number; ledger: DaaluuLedger;
  requiredHouses: number[];
  /** Room rule: replies must follow the led color. Missing = true. */
  followColor?: boolean;
  freedomHouses: number; freedomHousesBySeat?: number[];
  eligibleDebtors: number[]; bailoutSeats: number[]; bailoutBuyers: number[][]; processedBailoutSeats: number[];
  bailoutOffers: DaaluuBailoutOffer[]; bailoutLoans: DaaluuBailoutLoan[]; deadTileIds: string[];
  winningSeat: number | null;
  debtChoice: { remaining: number[]; surplus: number[] } | null;
  capturePiles: DaaluuCapturePile[][];
  houseTransfers: DaaluuHouseTransfer[];
  lowTrick: boolean;
  leaderSkips: DaaluuState['leaderSkips'];
  distribution: DaaluuDistribution;
}
export type DaaluuAction =
  | { type: 'call'; kindId: string }
  | { type: 'select'; stackHandle: string }
  | { type: 'collect'; stackHandle: string }
  | { type: 'pair-call'; color: DaaluuKind['color'] }
  | { type: 'tribute'; tileIds: string[]; returnsBySeat?: { slaveSeat: number; tileIds: string[] }[] }
  | { type: 'aravgui'; tileIds: string[] }
  | { type: 'debt-choice'; debtorSeat: number; pileId: string }
  | { type: 'play'; tileIds: string[] }
  | { type: 'bailout'; debtorSeat: number | null; lenderSeat?: number }
  | { type: 'bailout-loan'; buyerSeat: number; accept: boolean }
  | { type: 'bailout-choose'; buyerSeat: number };
export type DaaluuResult = { ok: true; state: GameState } | { ok: false; error: string };
const fail = (error: string): DaaluuResult => ({ ok: false, error });

export function createDaaluuGame(playerIds: string[], rng: Rng, options: {
  roundId: string; handles: string[]; callerSeat?: number; previous?: DaaluuState; followColor?: boolean;
}): GameState {
  const seats = playerIds.length;
  if (seats !== 5) throw new Error('Daaluu requires exactly five players.');
  if (new Set(playerIds).size !== seats) throw new Error('Duplicate Daaluu player.');
  const previous = options.previous;
  const ledger = previous ? structuredClone(previous.ledger) : {
    tea: Array(seats).fill(2), debt: Array.from({ length: seats }, () => Array(seats).fill(0)), masters: Array(seats).fill(null), slaveOrder: [],
  };
  ledger.slaveOrder ??= ledger.masters.flatMap((master, seat) => master === null ? [] : [seat]);
  const preferredCaller = options.callerSeat ?? (previous ? (previous.callerSeat + 1) % seats : 0);
  if (!Number.isInteger(preferredCaller) || preferredCaller < 0 || preferredCaller >= seats) throw new Error('Invalid caller or round identifier.');
  const callerSeat = Array.from({ length: seats }, (_, offset) => (preferredCaller + offset) % seats)
    .find(seat => ledger.masters[seat] === null);
  if (callerSeat === undefined) throw new Error('No free player can call Janlii.');
  const inventory: DaaluuTile[] = DAALUU_KINDS.flatMap(kind => Array.from({ length: kind.copies }, (_, i) => ({ id: `${kind.id}:${i}`, kindId: kind.id })));
  const reserve = inventory.filter(tile => daaluuKind(tile).copies === 4 && tile.kindId !== 'yooz' && tile.id.endsWith(':0'));
  const reserved = new Set(reserve.map(tile => tile.id));
  const playable = inventory.filter(tile => !reserved.has(tile.id));
  if (options.handles.length !== playable.length) throw new Error('Missing shuffle handles.');
  const shuffle = createDaaluuShuffle({ seats, callerSeat, roundId: options.roundId,
    tiles: playable.map((tile, i) => ({ tileId: tile.id, handle: options.handles[i] })) }, rng);
  const daaluu: DaaluuState = {
    stage: 'mixing', round: (previous?.round ?? 0) + 1, callerSeat, janlii: null, shuffle,
    inventory, reserve, ledger, hands: Array.from({ length: seats }, () => []), captured: Array.from({ length: seats }, () => []),
    houses: Array(seats).fill(0), earnedHouses: Array(seats).fill(0), capturePiles: Array.from({ length: seats }, () => []), houseTransfers: [], lowTrick: false, leaderSkips: null, quota: 2, trick: [], winningSeat: null, lastTrick: null,
    bailoutSeats: [], processedBailoutSeats: [], processedBailoutDebtors: [], bailoutOffers: [], bailoutLoans: [], dead: Array.from({ length: seats }, () => []),
    distribution: { startIndex: null, collected: 0, lastCollection: null }, pairCollection: null,
    originalPairHolder: null,
    tribute: null, tributeBatch: null, processedTributes: [], aravgui: null, aravguiSeats: [], processedAravguiSeats: [], debtChoice: null, motion: { serial: 0, movements: [] },
    followColor: options.followColor ?? previous?.followColor ?? true,
  };
  return { gameType: 'daaluu', format: { seats, cardsEach: 10, passesToClear: 0, removed: [] },
    playerIds: [...playerIds], hands: playerIds.map(() => []), dead: [], phase: 'playing', turn: null,
    trick: null, passes: 0, lastActions: playerIds.map(() => null), mustInclude: null, winner: null, daaluu };
}

export function eligibleDaaluuDebtors(d: DaaluuState): number[] {
  return d.ledger.debt.flatMap((debts, seat) => d.ledger.masters[seat] === null && debts.reduce((a, b) => a + b, 0) >= 5 ? [seat] : []);
}
function isPair(tiles: DaaluuTile[]) { return tiles.length === 2 && tiles[0].kindId === tiles[1].kindId; }
function isDaaluuUuluuLead(tiles: DaaluuTile[]) {
  return tiles.length === 2 && tiles.some(tile => tile.kindId === 'daaluu') && tiles.some(tile => tile.kindId === 'uuluu');
}
function isTopDoublePair(tiles: DaaluuTile[]) {
  if (tiles.length !== 4) return false;
  const counts = (kind: string) => tiles.filter(tile => tile.kindId === kind).length;
  return counts('daaluu') === 2 && counts('siiluu') === 2 ||
    counts('uuluu') === 2 && counts('bajgar') === 2;
}
function isTwoPairs(tiles: DaaluuTile[]) {
  if (tiles.length !== 4) return false;
  const counts = new Map<string, number>();
  for (const tile of tiles) counts.set(tile.kindId, (counts.get(tile.kindId) ?? 0) + 1);
  return counts.size === 2 && [...counts.values()].every(count => count === 2);
}
interface PublicJanlii { janlii: string | null; trick?: DaaluuPlay[]; capturePiles?: DaaluuCapturePile[][] }
function bothJanliiPlayed(d: PublicJanlii) {
  return d.janlii !== null &&
    [...(d.trick ?? []).flatMap(play => play.tiles), ...(d.capturePiles ?? []).flatMap(piles => piles.flatMap(pile => pile.tiles))]
      .filter(tile => tile.kindId === d.janlii).length === 2;
}
function tileColor(d: { janlii: string | null }, tile: DaaluuTile) {
  return tile.kindId === d.janlii ? null : daaluuKind(tile).color;
}
export function daaluuBeats(d: PublicJanlii & { lowTrick?: boolean; deadTileIds?: readonly string[] }, tiles: DaaluuTile[], current: DaaluuTile[]) {
  if (tiles.some(tile => d.deadTileIds?.includes(tile.id))) return false;
  if (tiles.length === 4 || current.length === 4) return false;
  if (tiles.length !== current.length || (tiles.length === 2 && !isPair(tiles))) return false;
  // Daaluu and Uuluu may start a two-color trick together, but they are singles,
  // never a pair that can overtake an ordinary pair.
  if (isDaaluuUuluuLead(current)) return false;
  if (tiles[0].kindId === d.janlii) return current[0].kindId !== d.janlii;
  return current[0].kindId !== d.janlii && (d.lowTrick || daaluuKind(tiles[0]).color === daaluuKind(current[0]).color) && power(d, tiles[0]) > power(d, current[0]);
}

export interface DaaluuPlayContext {
  hand: DaaluuTile[]; janlii: string | null; trick: DaaluuPlay[];
  winningSeat: number | null; deadTileIds: string[];
  lowTrick?: boolean; capturePiles?: DaaluuCapturePile[][];
  /** Missing = true (classic rule). */
  followColor?: boolean;
}
export function legalDaaluuHandPlays(d: DaaluuPlayContext): DaaluuTile[][] {
  const hand = d.hand;
  if (!hand?.length) return [];
  const width = d.trick[0]?.tiles.length;
  let candidates: DaaluuTile[][] = width === 2 || width === 4 ? [] : hand.map(tile => [tile]);
  if (width === 2 || width === undefined) {
    for (let i = 0; i < hand.length; i++) for (let j = i + 1; j < hand.length; j++) {
      const pair = [hand[i], hand[j]];
      if (width === 2 || isPair(pair) || (bothJanliiPlayed(d) && isDaaluuUuluuLead(pair))) candidates.push(pair);
    }
  }
  if (width === 4 || width === undefined) {
    for (let i = 0; i < hand.length; i++) for (let j = i + 1; j < hand.length; j++)
      for (let k = j + 1; k < hand.length; k++) for (let l = k + 1; l < hand.length; l++) {
        const four = [hand[i], hand[j], hand[k], hand[l]];
        if (width === 4 || isTopDoublePair(four)) candidates.push(four);
      }
  }
  if (width === undefined) {
    candidates = candidates.filter(tiles => !tiles.some(tile => d.deadTileIds.includes(tile.id)));
    if (!d.lowTrick) {
      candidates = candidates.filter(tiles => isPair(tiles) || isDaaluuUuluuLead(tiles) || isTopDoublePair(tiles) || tiles[0].kindId === d.janlii || daaluuKind(tiles[0]).rank >= 8);
    }
    return candidates;
  }
  const color = d.lowTrick ? null : tileColor(d, d.trick[0].tiles[0]);
  // Room rule "follow color" off: replies need not follow the led color, but holding a pair
  // (or two pairs) of the led color still obliges you to play it. Death is unchanged.
  const freeColor = d.followColor === false;
  if (width === 4) {
    const required = color === null ? 0 : Math.min(4, hand.filter(tile => tileColor(d, tile) === color).length);
    const followsColor = (tiles: DaaluuTile[]) => color === null || tiles.filter(tile => tileColor(d, tile) === color).length >= required;
    const pairReplies = candidates.filter(isTwoPairs).filter(followsColor);
    return pairReplies.length ? pairReplies : freeColor ? candidates : candidates.filter(followsColor);
  }
  const trumps = candidates.filter(tiles => (width === 1 || isPair(tiles)) && tiles[0].kindId === d.janlii);
  if (width === 2 && isDaaluuUuluuLead(d.trick[0].tiles)) {
    if (freeColor) return candidates;
    const red = Math.min(1, hand.filter(tile => tileColor(d, tile) === 'red').length);
    const white = Math.min(1, hand.filter(tile => tileColor(d, tile) === 'white').length);
    return candidates.filter(tiles => tiles.filter(tile => tileColor(d, tile) === 'red').length >= red &&
      tiles.filter(tile => tileColor(d, tile) === 'white').length >= white);
  }
  const pairs = width === 2 ? candidates.filter(tiles => isPair(tiles) && tileColor(d, tiles[0]) === color && color !== null) : [];
  const required = color === null ? 0 : Math.min(width, hand.filter(tile => tileColor(d, tile) === color).length);
  const following = pairs.length ? pairs : freeColor ? candidates : candidates.filter(tiles => color === null || tiles.filter(tile => tileColor(d, tile) === color).length >= required);
  // Janlii may be played anytime, but never counts toward a color obligation.
  return [...following, ...trumps.filter(trump => !following.includes(trump))];
}

/** Declining to beat kills only tiles that form a legal, live winning response. */
export function daaluuDyingTiles(d: DaaluuPlayContext, played: DaaluuTile[]): DaaluuTile[] {
  const current = d.trick.find(play => play.seat === d.winningSeat)?.tiles;
  if (!current || daaluuBeats(d, played, current)) return [];
  const ids = new Set(legalDaaluuHandPlays(d).filter(candidate => daaluuBeats(d, candidate, current)).flatMap(candidate => candidate.map(tile => tile.id)));
  return d.hand.filter(tile => ids.has(tile.id));
}

export function legalDaaluuPlays(d: DaaluuState, seat: number): DaaluuTile[][] {
  return legalDaaluuHandPlays({ hand: d.hands[seat], janlii: d.janlii, trick: d.trick,
    winningSeat: d.winningSeat, deadTileIds: d.dead[seat], lowTrick: d.lowTrick, capturePiles: d.capturePiles, followColor: d.followColor });
}

function chooseDaaluuLeader(state: GameState, preferred: number) {
  const d = state.daaluu!;
  d.lowTrick = false;
  const skipped: number[] = [];
  for (let offset = 0; offset < d.hands.length; offset++) {
    const seat = (preferred + offset) % d.hands.length;
    if (legalDaaluuPlays(d, seat).length) {
      state.turn = skipped[0] ?? seat;
      d.leaderSkips = skipped.length ? { serial: d.motion.serial, seats: skipped } : null;
      return;
    }
    skipped.push(seat);
  }
  // Everybody has only low singles: finish normally, highest rank wins across colors.
  d.lowTrick = true;
  const lowSkipped: number[] = [];
  for (let offset = 0; offset < d.hands.length; offset++) {
    const seat = (preferred + offset) % d.hands.length;
    if (legalDaaluuPlays(d, seat).length) {
      state.turn = lowSkipped[0] ?? seat;
      d.leaderSkips = lowSkipped.length ? { serial: d.motion.serial, seats: lowSkipped } : null;
      return;
    }
    lowSkipped.push(seat);
  }
  // No living tile remains. The user-confirmed last winner takes the remainder.
  if (d.hands.some(hand => hand.length > 0)) {
    captureDaaluuTrick(d, preferred, d.hands.map((tiles, seat) => ({ seat, tiles: [...tiles], deadTileIds: tiles.map(tile => tile.id) })));
    d.hands = d.hands.map(() => []);
  }
  state.turn = null;
  d.leaderSkips = null;
}

/** Advance one announced starter skip. The server owns the delay between calls. */
export function advanceDaaluuLeaderSkip(state: GameState): GameState {
  const skipped = state.daaluu?.leaderSkips;
  if (!skipped?.seats.length || state.daaluu?.stage !== 'playing' || state.turn !== skipped.seats[0])
    throw new Error('No Daaluu starter skip is active.');
  const next = structuredClone(state), d = next.daaluu!;
  next.turn = (next.turn! + 1) % d.hands.length;
  d.leaderSkips = skipped.seats.length > 1
    ? { serial: skipped.serial, seats: skipped.seats.slice(1) } : null;
  return next;
}

function captureDaaluuTrick(d: DaaluuState, winnerSeat: number, plays: DaaluuPlay[], countsTowardFreedom = true) {
  const owner = ultimateDaaluuMaster(d, winnerSeat);
  d.motion.movements.push(...plays.map(play => ({ tiles: [...play.tiles], fromSeat: play.seat, toSeat: owner,
    from: 'board' as const, to: 'capture' as const, phase: 1 as const })));
  const ordered = [...plays.filter(play => play.seat !== winnerSeat), plays.find(play => play.seat === winnerSeat)!];
  for (let column = 0; column < plays[0].tiles.length; column++) {
    const tiles = ordered.map(play => play.tiles[column]);
    const deadTileIds = ordered.flatMap(play => play.deadTileIds ?? []).filter(id => tiles.some(tile => tile.id === id));
    const slot = Math.max(-1, ...d.capturePiles[owner].map((pile, index) => pile.slot ?? index)) + 1;
    d.capturePiles[owner].push({ tiles, deadTileIds, slot }); d.captured[owner].push(...tiles);
  }
  // Assisted Aravgui houses belong to the master and cannot buy a slave's freedom.
  if (countsTowardFreedom) d.earnedHouses[winnerSeat] += plays[0].tiles.length;
  d.houses[owner] = d.captured[owner].length / d.hands.length;
  d.lastTrick = { winnerSeat, plays };
}

function creditDaaluuTea(d: DaaluuState, seat: number, amount: number) {
  d.ledger.tea[ultimateDaaluuMaster(d, seat)] += amount;
}

function makeDaaluuSlave(d: DaaluuState, slave: number, master: number) {
  d.ledger.masters[slave] = master;
  d.ledger.slaveOrder.push(slave);
  const owner = ultimateDaaluuMaster(d, slave);
  d.ledger.tea[owner] += d.ledger.tea[slave];
  d.ledger.tea[slave] = 0;
  d.ledger.debt[slave].fill(0);
  // Claims earned before enslavement belong to the master permanently, even
  // if the slave later wins freedom. A debt the master owed the slave cancels.
  for (let debtor = 0; debtor < d.ledger.debt.length; debtor++) {
    const owed = d.ledger.debt[debtor][slave];
    if (debtor !== owner) d.ledger.debt[debtor][owner] += owed;
    d.ledger.debt[debtor][slave] = 0;
  }
}

function checkDaaluuWinner(state: GameState) {
  const d = state.daaluu!;
  const winner = d.ledger.tea.findIndex(tea => tea === d.reserve.length);
  if (winner < 0) return false;
  d.stage = 'finished'; state.phase = 'finished'; state.winner = winner; state.turn = null;
  return true;
}

function startOrdinaryDaaluuPlay(state: GameState) {
  const d = state.daaluu!;
  if (d.hands.every(hand => hand.length === 0)) { settleDaaluuRound(state); return; }
  d.stage = 'playing';
  state.turn = d.callerSeat;
  // Lucky hands preempt the ordinary caller. Seat order resolves multiple claims.
  const lucky = d.hands.findIndex(hand => hand.filter(tile => daaluuKind(tile).rank === 9).length === 4 || hand.filter(tile => tile.kindId === 'yooz').length === 4);
  if (lucky >= 0) state.turn = lucky;
  chooseDaaluuLeader(state, state.turn!);
}

function beginNextAravgui(state: GameState) {
  const d = state.daaluu!;
  const holderSeat = d.aravguiSeats.find(seat => !d.processedAravguiSeats.includes(seat));
  if (holderSeat === undefined) { d.aravgui = null; startOrdinaryDaaluuPlay(state); return; }
  d.aravgui = { holderSeat, plays: [] };
  d.stage = 'aravgui-call'; state.turn = holderSeat;
}

function beginPairCollection(state: GameState) {
  const d = state.daaluu!;
  const holder = d.originalPairHolder;
  // The pair must survive tribute: given away or acquired Janlii never triggers.
  if (holder !== null && d.hands[holder].filter(tile => tile.kindId === d.janlii).length === 2) {
    d.stage = 'pair-call'; state.turn = holder; return;
  }
  d.pairCollection = null;
  beginNextAravgui(state);
}

function beginDaaluuPlay(state: GameState) {
  const d = state.daaluu!;
  const firstSeat = daaluuMasterOrder(d).reverse().find(seat => d.ledger.masters[seat] !== null && !d.processedTributes.includes(seat));
  if (firstSeat !== undefined) {
    const masterSeat = ultimateDaaluuMaster(d, firstSeat);
    const siblings = d.ledger.slaveOrder.filter(slave => d.ledger.masters[slave] === masterSeat && !d.processedTributes.includes(slave));
    if (siblings.length > 1) {
      d.tributeBatch = { masterSeat, firstSlaveSeat: siblings[0], slaveSeats: siblings, phase: 'giving', giveIndex: 0,
        relayIndex: 1, offers: siblings.map(slaveSeat => ({ slaveSeat, offered: [], big: [], middle: [], returned: [] })), masterReturns: [] };
      beginDaaluuTributeBatchGive(state, isDaaluuBatchOffer, beginDaaluuPlay);
      return;
    }
  }
  // The first slave trades with the master before taking a newer slave's tiles.
  for (const seat of daaluuMasterOrder(d).reverse().filter(seat => !d.processedTributes.includes(seat))) {
    const master = d.ledger.masters[seat];
    if (master === null) { d.processedTributes.push(seat); continue; }
    const relayMaster = d.ledger.masters[master] === null ? null : ultimateDaaluuMaster(d, master);
    const required = d.hands[seat].filter(tile => tile.kindId === d.janlii || daaluuKind(tile).rank >= (relayMaster === null ? 10 : 8));
    if (!required.length) { d.processedTributes.push(seat); continue; }
    d.tribute = { slaveSeat: seat, masterSeat: master, offered: [], returned: [],
      ...(relayMaster === null ? {} : { relay: { masterSeat: relayMaster, offered: [], returned: [] } }) };
    d.stage = 'tribute-give'; state.turn = seat; return;
  }
  d.tribute = null;
  beginPairCollection(state);
}

function isDaaluuBatchOffer(tile: { kindId: string }, janlii: string | null, relay: boolean) {
  if (tile.kindId === janlii) return true;
  const rank = daaluuKind(tile as DaaluuTile).rank;
  return relay ? rank >= 8 && rank < 10 : rank >= 10;
}

/** The server advances this stage after every player has had time to arrange their dealt hand. */
export function completeDaaluuArranging(state: GameState): GameState {
  if (state.daaluu?.stage !== 'arranging') throw new Error('Daaluu arranging is not active.');
  const next = structuredClone(state);
  beginDaaluuPlay(next);
  return next;
}

function prepareDaaluuBailout(state: GameState) {
  const d = state.daaluu!;
  const debtors = eligibleDaaluuDebtors(d);
  d.bailoutSeats = [...new Set(debtors.flatMap(debtor => bailoutCandidateBuyers(d, debtor)))].sort((a, b) => a - b);
  d.processedBailoutSeats = []; d.processedBailoutDebtors = []; d.bailoutOffers = []; d.bailoutLoans = [];
  if (d.bailoutSeats.length) { d.stage = 'bailout'; state.turn = null; }
  else finishDaaluuRound(state);
}

function bailoutPayment(d: DaaluuState, buyer: number, debtor: number) {
  return d.ledger.debt[debtor].reduce((sum, debt, creditor) => sum + (creditor === buyer ? 0 : debt), 0);
}

function bailoutCandidateBuyers(d: DaaluuState, debtor: number) {
  const free = d.ledger.tea.flatMap((_, seat) => seat !== debtor && d.ledger.masters[seat] === null ? [seat] : []);
  const funded = free.filter(seat => bailoutPayment(d, seat, debtor) <= d.ledger.tea[seat]);
  return funded.length ? funded : free;
}

function finishDaaluuBailoutResponse(state: GameState, buyer: number, offer?: DaaluuBailoutOffer) {
  const d = state.daaluu!;
  d.processedBailoutSeats.push(buyer);
  if (offer) d.bailoutOffers.push(offer);
  d.stage = 'bailout'; state.turn = null;
  if (d.bailoutSeats.every(seat => d.processedBailoutSeats.includes(seat)) && d.bailoutLoans.length === 0) advanceDaaluuBailoutChoice(state);
}

function finishDaaluuRound(state: GameState) {
  state.daaluu!.stage = 'round-over';
  state.turn = null;
}

function fundableDaaluuOffer(d: DaaluuState, offer: DaaluuBailoutOffer) {
  if (d.ledger.masters[offer.buyerSeat] !== null || d.ledger.masters[offer.debtorSeat] !== null) return false;
  const payment = bailoutPayment(d, offer.buyerSeat, offer.debtorSeat);
  const shortage = Math.max(0, payment - d.ledger.tea[offer.buyerSeat]);
  return shortage === 0 || offer.lenderSeat !== undefined && offer.loanAmount !== undefined &&
    shortage <= offer.loanAmount && d.ledger.tea[offer.lenderSeat] >= offer.loanAmount;
}

function advanceDaaluuBailoutChoice(state: GameState) {
  const d = state.daaluu!;
  const debtor = eligibleDaaluuDebtors(d).find(seat => !d.processedBailoutDebtors.includes(seat));
  if (debtor === undefined) { finishDaaluuRound(state); return; }
  const offers = d.bailoutOffers.filter(offer => offer.debtorSeat === debtor && fundableDaaluuOffer(d, offer));
  if (offers.length === 0) { d.processedBailoutDebtors.push(debtor); advanceDaaluuBailoutChoice(state); }
  else if (offers.length === 1) completeDaaluuBailout(state, offers[0]);
  else { d.stage = 'bailout-choice'; state.turn = debtor; }
}

function completeDaaluuBailout(state: GameState, offer: DaaluuBailoutOffer) {
  const d = state.daaluu!;
  const { buyerSeat: buyer, debtorSeat: debtor } = offer;
  const owed = [...d.ledger.debt[debtor]];
  const payment = bailoutPayment(d, buyer, debtor);
  if (offer.lenderSeat !== undefined && offer.loanAmount !== undefined && d.ledger.tea[buyer] < payment) {
    d.ledger.tea[offer.lenderSeat] -= offer.loanAmount;
    d.ledger.tea[buyer] += offer.loanAmount;
    d.ledger.debt[buyer][offer.lenderSeat] += offer.loanAmount;
  }
  if (d.ledger.tea[buyer] < payment) throw new Error('Daaluu bailout has insufficient tea.');
  d.ledger.tea[buyer] -= payment;
  owed.forEach((debt, creditor) => { if (creditor !== buyer) creditDaaluuTea(d, creditor, debt); });
  makeDaaluuSlave(d, debtor, buyer);
  d.processedBailoutDebtors.push(debtor);
  if (!checkDaaluuWinner(state)) advanceDaaluuBailoutChoice(state);
}

export function applyDaaluuAction(state: GameState, seat: number, action: DaaluuAction): DaaluuResult {
  const d = state.daaluu;
  if (!d || state.phase !== 'playing' || (!['debt-choice', 'bailout', 'bailout-loan'].includes(action.type) && state.turn !== seat)) return fail('It is not your Daaluu turn.');
  if (d.leaderSkips) return fail('Waiting for the next Daaluu starter.');
  const next = structuredClone(state), n = next.daaluu!;
  n.motion = { serial: (d.motion?.serial ?? 0) + 1, movements: [] };
  if (action.type === 'call') {
    if (d.ledger.masters[seat] !== null) return fail('Slaves cannot call Janlii.');
    if (d.stage !== 'calling' || !DAALUU_KINDS.some(kind => kind.id === action.kindId && kind.copies === 2)) return fail('Choose one of the eight eligible жанлий tiles.');
    n.janlii = action.kindId; n.stage = 'selecting';
  } else if (action.type === 'select' || action.type === 'collect') {
    if ((action.type === 'select' && d.stage !== 'selecting') || (action.type === 'collect' && d.stage !== 'distributing')) return fail('Stack collection is not active.');
    const index = daaluuShuffleViewFor(d.shuffle).stacks.findIndex(stack => stack.handle === action.stackHandle);
    if (index < 0) return fail('Choose a stack on this table.');
    if (action.type === 'collect' && index !== (d.distribution.startIndex! + d.distribution.collected) % d.shuffle.stacks.length) return fail('Collect the next stack clockwise.');
    if (action.type === 'select') n.distribution.startIndex = index;
    const map = new Map(n.inventory.map(tile => [tile.id, tile]));
    n.hands[seat].push(...n.shuffle.stacks[index].map(id => ({ ...map.get(id)! })));
    n.distribution.collected++;
    n.distribution.lastCollection = { index, seat, serial: n.distribution.collected };
    if (n.distribution.collected < n.shuffle.stacks.length) {
      n.stage = 'distributing'; next.turn = (seat + 1) % n.hands.length;
      return { ok: true, state: next };
    }
    // Eligibility is fixed from the original ten-tile deal, before tribute or pair collection.
    // A master (a seat with at least one slave) never qualifies: tribute brings it the slave's big tiles.
    n.aravguiSeats = n.hands.flatMap((hand, originalSeat) =>
      isDaaluuAravguiHand(hand, n.janlii) && !n.ledger.masters.includes(originalSeat) ? [originalSeat] : []);
    // The Janlii color call happens after the master-slave exchange, so only
    // snapshot the original holder here; tribute may still take the pair away.
    const pairHolder = n.hands.findIndex(hand => hand.filter(tile => tile.kindId === n.janlii).length === 2);
    n.originalPairHolder = pairHolder >= 0 ? pairHolder : null;
    n.stage = 'arranging'; next.turn = null;
  } else if (action.type === 'pair-call') {
    if (d.stage !== 'pair-call' || (action.color !== 'red' && action.color !== 'white')) return fail('Choose red or white for the dealt Janlii pair.');
    if (d.originalPairHolder !== null && seat !== d.originalPairHolder) return fail('Only the originally dealt Janlii-pair holder may call.');
    const tiles = n.hands[seat].filter(tile => tile.kindId === n.janlii);
    if (tiles.length !== 2) return fail('A dealt Janlii pair is required.');
    n.pairCollection = { holderSeat: seat, color: action.color };
    n.hands[seat] = n.hands[seat].filter(tile => tile.kindId !== n.janlii);
    n.trick = [{ seat, tiles }]; n.winningSeat = seat;
    n.motion.movements.push({ tiles, fromSeat: seat, toSeat: seat, from: 'hand', to: 'board', phase: 0 });
    n.stage = 'pair-response'; next.turn = (seat + 1) % n.hands.length;
  } else if (action.type === 'bailout') {
    if (d.stage !== 'bailout' || !d.bailoutSeats.includes(seat) || d.processedBailoutSeats.includes(seat) ||
      d.bailoutLoans.some(loan => loan.buyerSeat === seat) || d.ledger.masters[seat] !== null)
      return fail('Debt acquisition is not available to this player.');
    const debtor = action.debtorSeat;
    if (debtor !== null) {
      if (!eligibleDaaluuDebtors(n).includes(debtor) || !bailoutCandidateBuyers(n, debtor).includes(seat)) return fail('That player is not eligible.');
      const payment = bailoutPayment(n, seat, debtor);
      const shortage = Math.max(0, payment - n.ledger.tea[seat]);
      if (shortage) {
        const lender = action.lenderSeat;
        if (!Number.isInteger(lender) || lender === seat || lender === debtor || lender! < 0 || lender! >= n.hands.length || n.ledger.masters[lender!] !== null || n.ledger.tea[lender!] < shortage)
          return fail('Choose a free lender with enough room tea.');
        n.bailoutLoans.push({ buyerSeat: seat, debtorSeat: debtor, lenderSeat: lender!, amount: shortage });
      } else finishDaaluuBailoutResponse(next, seat, { buyerSeat: seat, debtorSeat: debtor });
    } else finishDaaluuBailoutResponse(next, seat);
  } else if (action.type === 'bailout-loan') {
    const loan = d.bailoutLoans.find(item => item.buyerSeat === action.buyerSeat && item.lenderSeat === seat);
    if (d.stage !== 'bailout' || !loan || typeof action.accept !== 'boolean')
      return fail('No room-tea loan awaits your decision.');
    n.bailoutLoans = n.bailoutLoans.filter(item => item.buyerSeat !== action.buyerSeat);
    if (action.accept) {
      if (n.ledger.masters[seat] !== null || n.ledger.tea[seat] < loan.amount || !eligibleDaaluuDebtors(n).includes(loan.debtorSeat)) return fail('The loan is no longer available.');
      finishDaaluuBailoutResponse(next, loan.buyerSeat,
        { buyerSeat: loan.buyerSeat, debtorSeat: loan.debtorSeat, lenderSeat: seat, loanAmount: loan.amount });
    }
  } else if (action.type === 'bailout-choose') {
    if (d.stage !== 'bailout-choice' || !Number.isInteger(action.buyerSeat)) return fail('No master choice is active.');
    const offer = d.bailoutOffers.find(item => item.debtorSeat === seat && item.buyerSeat === action.buyerSeat);
    if (!offer || !fundableDaaluuOffer(n, offer)) return fail('Choose an available buyer.');
    completeDaaluuBailout(next, offer);
  } else if (action.type === 'debt-choice') {
    const choice = n.debtChoice;
    if (d.stage !== 'debt-choice' || !choice) return fail('House placement is not active.');
    const debtor = action.debtorSeat;
    if (choice.surplus[seat] <= 0) return fail('You have no surplus house.');
    if (!Number.isInteger(debtor) || debtor < 0 || debtor >= n.hands.length || choice.remaining[debtor] <= 0)
      return fail('Choose a player who still needs a house.');
    if (!n.capturePiles[seat].some(pile => pile.tiles[0]?.id === action.pileId)) return fail('Choose one of your available houses.');
    transferDaaluuHouse(n, debtor, seat, action.pileId);
    choice.surplus[seat]--; choice.remaining[debtor]--;
    advanceDebtChoice(next);
  } else if (action.type === 'tribute') {
    const maxTributeTiles = d.tributeBatch?.phase === 'master-return' ? 40 : 10;
    if (!d.tribute || (d.stage !== 'tribute-give' && d.stage !== 'tribute-return') || !Array.isArray(action.tileIds) || action.tileIds.length < 1 || action.tileIds.length > maxTributeTiles || new Set(action.tileIds).size !== action.tileIds.length) return fail('Select the required exchange tiles.');
    const selected = d.hands[seat].filter(tile => action.tileIds.includes(tile.id));
    if (selected.length !== action.tileIds.length) return fail('Choose tiles from your own hand.');
    if (d.tributeBatch?.phase === 'giving' && d.stage === 'tribute-give') {
      const batch = n.tributeBatch!, offer = batch.offers[batch.giveIndex];
      const required = daaluuRequiredTributeTiles(d.hands[seat], d.janlii, false);
      if (!offer || seat !== offer.slaveSeat || required.length !== selected.length || !required.every(tile => action.tileIds.includes(tile.id))) return fail('Give every required high tile and Janlii.');
      offer.offered = selected; offer.big = selected.filter(tile => tile.kindId === d.janlii || daaluuKind(tile).rank >= 10);
      offer.middle = selected.filter(tile => !offer.big.some(big => big.id === tile.id));
      n.tribute!.offered = selected;
      n.hands[seat] = n.hands[seat].filter(tile => !action.tileIds.includes(tile.id));
      n.motion.movements.push({ tiles: selected, fromSeat: seat, toSeat: seat, from: 'hand', to: 'board', phase: 0 });
      batch.giveIndex++; beginDaaluuTributeBatchGive(next, isDaaluuBatchOffer, beginDaaluuPlay);
      return { ok: true, state: next };
    }
    if (d.tributeBatch?.phase === 'relay-give' && d.stage === 'tribute-give') {
      const batch = n.tributeBatch!, offer = batch.offers[batch.relayIndex];
      const required = daaluuBatchRelayTiles(d.hands[seat], d.janlii);
      if (!offer || seat !== offer.slaveSeat || required.length !== selected.length || !required.every(tile => action.tileIds.includes(tile.id)))
        return fail('Give every eight and nine to the first slave.');
      offer.middle = selected; offer.offered = [...offer.big, ...selected];
      n.hands[seat] = n.hands[seat].filter(tile => !action.tileIds.includes(tile.id));
      n.motion.movements.push({ tiles: selected, fromSeat: seat, toSeat: seat, from: 'hand', to: 'board', phase: 0 });
      beginDaaluuTributeBatchRelayReturn(next);
      return { ok: true, state: next };
    }
    if (d.tributeBatch?.phase === 'master-return' && d.stage === 'tribute-return') {
      const batch = n.tributeBatch!, expected = batch.offers.reduce((total, offer) => total + offer.big.length, 0);
      if (seat !== batch.masterSeat || selected.length !== expected || !validDaaluuTributeReturn(d.hands[seat], d.janlii, expected, action.tileIds, 10)) return fail('Return an equal number of eligible tiles to both slaves.');
      const suppliedRows = action.returnsBySeat;
      const rows = suppliedRows ?? batch.offers.map(offer => ({ slaveSeat: offer.slaveSeat, tileIds: [] as string[] }));
      const offeredRows = batch.offers.filter(offer => offer.big.length > 0);
      if (suppliedRows && (rows.length !== offeredRows.length || rows.some(row =>
        !offeredRows.some(offer => offer.slaveSeat === row.slaveSeat && offer.big.length === row.tileIds.length))))
        return fail('Give each slave exactly as many tiles as they offered.');
      if (!suppliedRows) {
        let offset = 0;
        for (const row of rows) {
          const count = batch.offers.find(offer => offer.slaveSeat === row.slaveSeat)!.big.length;
          row.tileIds = selected.slice(offset, offset + count).map(tile => tile.id); offset += count;
        }
      }
      const rowIds = rows.flatMap(row => row.tileIds);
      if (rowIds.length !== expected || new Set(rowIds).size !== expected ||
        rowIds.some(id => !action.tileIds.includes(id)) || action.tileIds.some(id => !rowIds.includes(id)))
        return fail('Assign every returned tile to one slave.');
      const returnedBySeat = new Map(rows.map(row => [row.slaveSeat, row.tileIds.map(id => selected.find(tile => tile.id === id)!)]));
      for (const offer of batch.offers) offer.returned = returnedBySeat.get(offer.slaveSeat) ?? [];
      batch.masterReturns = batch.offers.flatMap(offer => offer.returned);
      n.tribute!.returned = batch.masterReturns;
      n.hands[seat] = n.hands[seat].filter(tile => !action.tileIds.includes(tile.id));
      n.motion.movements.push({ tiles: selected, fromSeat: seat, toSeat: seat, from: 'hand', to: 'board', phase: 0 });
      n.stage = 'tribute-reveal'; next.turn = null;
      return { ok: true, state: next };
    }
    if (d.tributeBatch?.phase === 'relay-return' && d.stage === 'tribute-return') {
      const batch = n.tributeBatch!, offer = batch.offers[batch.relayIndex];
      if (!offer || seat !== batch.firstSlaveSeat || selected.length !== offer.middle.length ||
        !validDaaluuTributeReturn(d.hands[seat], d.janlii, offer.middle.length, action.tileIds, 8)) return fail('Return one small tile for each eight or nine received.');
      n.tribute!.returned = selected;
      n.hands[seat] = n.hands[seat].filter(tile => !action.tileIds.includes(tile.id));
      n.motion.movements.push({ tiles: selected, fromSeat: seat, toSeat: seat, from: 'hand', to: 'board', phase: 0 });
      n.stage = 'tribute-relay-reveal'; next.turn = null;
      return { ok: true, state: next };
    }
    if (d.stage === 'tribute-give') {
      const required = daaluuRequiredTributeTiles(d.hands[seat], d.janlii, !!d.tribute.relay);
      if (seat !== d.tribute.slaveSeat || required.length !== selected.length || !required.every(tile => action.tileIds.includes(tile.id))) return fail('Give every required high tile and Janlii.');
      n.tribute!.offered = selected;
      if (n.tribute!.relay) n.tribute!.relay!.offered = selected.filter(tile => tile.kindId === n.janlii || daaluuKind(tile).rank >= 10);
      n.hands[seat] = n.hands[seat].filter(tile => !action.tileIds.includes(tile.id));
      n.motion.movements.push({ tiles: selected, fromSeat: seat, toSeat: seat, from: 'hand', to: 'board', phase: 0 });
      n.stage = 'tribute-return'; next.turn = n.tribute!.relay?.offered.length ? n.tribute!.relay!.masterSeat : d.tribute.masterSeat;
    } else {
      const returningRelay = !!d.tribute.relay?.offered.length && !d.tribute.relay.returned.length;
      const expectedSeat = returningRelay ? d.tribute.relay!.masterSeat : d.tribute.masterSeat;
      const expectedCount = returningRelay ? d.tribute.relay!.offered.length : d.tribute.offered.length;
      if (seat !== expectedSeat || !validDaaluuTributeReturn(d.hands[seat], d.janlii, expectedCount, action.tileIds, returningRelay ? 10 : d.tribute.relay ? 8 : 10))
        return fail('Return an equal number of the lowest eligible tiles.');
      n.hands[seat] = n.hands[seat].filter(tile => !action.tileIds.includes(tile.id));
      if (returningRelay) n.tribute!.relay!.returned = selected; else n.tribute!.returned = selected;
      n.motion.movements.push({ tiles: selected, fromSeat: seat, toSeat: seat, from: 'hand', to: 'board', phase: 0 });
      n.stage = returningRelay ? 'tribute-relay-reveal' : 'tribute-reveal'; next.turn = null;
    }
  } else if (action.type === 'aravgui') {
    if (!d.aravgui || (d.stage !== 'aravgui-call' && d.stage !== 'aravgui-response') ||
      !Array.isArray(action.tileIds) || action.tileIds.length !== 2 || new Set(action.tileIds).size !== 2) return fail('Select two tiles for Аравгүй арав.');
    const selected = d.hands[seat].filter(tile => action.tileIds.includes(tile.id));
    if (selected.length !== 2) return fail('Choose two tiles from your hand.');
    if (d.stage === 'aravgui-call') {
      if (seat !== d.aravgui.holderSeat || !d.aravguiSeats.includes(seat)) return fail('Only the qualifying player may start Аравгүй арав.');
      n.hands[seat] = n.hands[seat].filter(tile => !action.tileIds.includes(tile.id));
      n.dead[seat] = [...new Set([...n.dead[seat], ...n.hands[seat].map(tile => tile.id)])];
      n.aravgui!.plays.push({ seat, tiles: selected });
      n.stage = 'aravgui-response'; next.turn = (seat + 1) % n.hands.length;
    } else {
      if (seat === d.aravgui.holderSeat || d.aravgui.plays.some(play => play.seat === seat) ||
        !legalDaaluuAravguiContributions(d.hands[seat], d.janlii)
          .some(pair => pair.every(tile => action.tileIds.includes(tile.id)))) return fail('Give two eligible tiles for Аравгүй арав.');
      n.hands[seat] = n.hands[seat].filter(tile => !action.tileIds.includes(tile.id));
      n.aravgui!.plays.push({ seat, tiles: selected, deadTileIds: selected.filter(tile => n.dead[seat].includes(tile.id)).map(tile => tile.id) });
      if (n.aravgui!.plays.length === n.hands.length) {
        captureDaaluuTrick(n, d.aravgui.holderSeat, n.aravgui!.plays, false);
        n.processedAravguiSeats.push(d.aravgui.holderSeat);
        beginNextAravgui(next);
      } else next.turn = (seat + 1) % n.hands.length;
    }
    n.motion.movements.unshift({ tiles: selected, fromSeat: seat, toSeat: seat, from: 'hand', to: 'board', phase: 0 });
  } else if (action.type === 'play') {
    if (d.stage === 'pair-response') {
      if (!d.pairCollection || !Array.isArray(action.tileIds) || action.tileIds.length !== 2 || new Set(action.tileIds).size !== 2) return fail('Select two tiles for the Janlii collection.');
      const selected = legalDaaluuPairContributions(d.hands[seat], d.pairCollection.color)
        .find(tiles => tiles.every(tile => action.tileIds.includes(tile.id)));
      if (!selected) return fail('Give your strongest pair of the chosen color, or follow that color as far as possible.');
      n.hands[seat] = n.hands[seat].filter(tile => !action.tileIds.includes(tile.id));
      n.trick.push({ seat, tiles: selected });
      n.motion.movements.push({ tiles: selected, fromSeat: seat, toSeat: seat, from: 'hand', to: 'board', phase: 0 });
      if (n.trick.length === n.hands.length) {
        captureDaaluuTrick(n, d.pairCollection.holderSeat, n.trick);
        n.trick = []; n.winningSeat = null; n.pairCollection = null;
        beginNextAravgui(next);
      } else next.turn = (seat + 1) % n.hands.length;
      return { ok: true, state: next };
    }
    if (d.stage !== 'playing' || !Array.isArray(action.tileIds) || action.tileIds.length < 1 || action.tileIds.length > 4 || action.tileIds.length === 3 || new Set(action.tileIds).size !== action.tileIds.length) return fail('Select one tile, a pair, or the four-tile top combination.');
    const selected = d.hands[seat].filter(tile => action.tileIds.includes(tile.id));
    if (selected.length !== action.tileIds.length || !legalDaaluuPlays(d, seat).some(play => play.length === selected.length && play.every(tile => action.tileIds.includes(tile.id)))) return fail('Follow color and pair requirements. Dead tiles cannot lead.');
    const dying = daaluuDyingTiles({ hand: d.hands[seat], janlii: d.janlii, trick: d.trick, winningSeat: d.winningSeat, deadTileIds: d.dead[seat], lowTrick: d.lowTrick, capturePiles: d.capturePiles, followColor: d.followColor }, selected);
    n.dead[seat] = [...new Set([...n.dead[seat], ...dying.map(tile => tile.id)])];
    n.hands[seat] = n.hands[seat].filter(tile => !action.tileIds.includes(tile.id));
    const winner = n.trick.find(play => play.seat === n.winningSeat);
    n.trick.push({ seat, tiles: selected, deadTileIds: selected.filter(tile => n.dead[seat].includes(tile.id)).map(tile => tile.id) });
    n.motion.movements.push({ tiles: selected, fromSeat: seat, toSeat: seat, from: 'hand', to: 'board', phase: 0 });
    if (!winner || daaluuBeats({ ...n, deadTileIds: n.dead[seat] }, selected, winner.tiles)) n.winningSeat = seat;
    if (n.trick.length === n.hands.length) {
      const winnerSeat = n.winningSeat!;
      captureDaaluuTrick(n, winnerSeat, n.trick); n.trick = []; n.winningSeat = null;
      chooseDaaluuLeader(next, winnerSeat);
      if (n.hands.every(hand => hand.length === 0)) settleDaaluuRound(next);
    } else next.turn = (seat + 1) % n.hands.length;
  } else return fail('Invalid Daaluu action.');
  return { ok: true, state: next };
}

/** The server calls this after both public exchange sets have rested on the table. */
export function completeDaaluuTribute(state: GameState): GameState {
  const d = state.daaluu;
  if (!d || !d.tribute || (d.stage !== 'tribute-reveal' && d.stage !== 'tribute-relay-reveal'))
    throw new Error('No completed Daaluu tribute to transfer.');
  const next = structuredClone(state), n = next.daaluu!, tribute = n.tribute!;
  if (d.tributeBatch?.phase === 'master-return' && d.stage === 'tribute-reveal') {
    if (d.tributeBatch.masterReturns.length !== d.tributeBatch.offers.reduce((total, offer) => total + offer.big.length, 0))
      throw new Error('The master return does not match the combined high-tile offers.');
    completeDaaluuTributeBatchMaster(next, isDaaluuBatchOffer, beginDaaluuPlay);
    return next;
  }
  if (d.tributeBatch?.phase === 'relay-return' && d.stage === 'tribute-relay-reveal') {
    completeDaaluuTributeBatchRelay(next, isDaaluuBatchOffer, beginDaaluuPlay);
    return next;
  }
  if (d.stage === 'tribute-relay-reveal') {
    const relay = tribute.relay;
    if (!relay?.offered.length || relay.returned.length !== relay.offered.length)
      throw new Error('No completed Daaluu relay to transfer.');
    const middle = tribute.offered.filter(tile => !relay.offered.some(high => high.id === tile.id));
    n.hands[relay.masterSeat].push(...relay.offered);
    n.hands[tribute.masterSeat].push(...middle, ...relay.returned);
    const movements: DaaluuMovement[] = [
      { tiles: relay.offered, fromSeat: tribute.slaveSeat, toSeat: relay.masterSeat, from: 'board', to: 'hand', phase: 0 },
      { tiles: middle, fromSeat: tribute.slaveSeat, toSeat: tribute.masterSeat, from: 'board', to: 'hand', phase: 0 },
      { tiles: relay.returned, fromSeat: relay.masterSeat, toSeat: tribute.masterSeat, from: 'board', to: 'hand', phase: 0 },
    ];
    n.motion = { serial: d.motion.serial + 1, movements: movements.filter(move => move.tiles.length > 0) };
    n.stage = 'tribute-return'; next.turn = tribute.masterSeat;
    return next;
  }
  if (!tribute.returned.length || tribute.returned.length !== tribute.offered.length)
    throw new Error('No completed Daaluu tribute to transfer.');
  if (!tribute.relay) n.hands[tribute.masterSeat].push(...tribute.offered);
  n.hands[tribute.slaveSeat].push(...tribute.returned);
  n.motion = { serial: d.motion.serial + 1, movements: [
    ...(!tribute.relay ? [{ tiles: tribute.offered, fromSeat: tribute.slaveSeat, toSeat: tribute.masterSeat, from: 'board' as const, to: 'hand' as const, phase: 0 as const }] : []),
    { tiles: tribute.returned, fromSeat: tribute.masterSeat, toSeat: tribute.slaveSeat, from: 'board', to: 'hand', phase: 0 },
  ] };
  n.processedTributes.push(tribute.slaveSeat);
  beginDaaluuPlay(next);
  return next;
}

function settleDaaluuRound(state: GameState) {
  const d = state.daaluu!;
  // Captures and obligations belong to the master throughout this round.
  // Earned freedom changes the relationship only after this settlement.
  const quotas = daaluuRequiredHouses(d);
  d.debtChoice = {
    remaining: d.houses.map((houses, seat) => Math.max(0, quotas[seat] - houses)),
    surplus: d.houses.map((houses, seat) => Math.max(0, houses - quotas[seat])),
    shortOwnHouses: d.houses.flatMap((houses, seat) => houses < DAALUU_FREEDOM_HOUSES ? [seat] : []),
  };
  advanceDebtChoice(state);
}

/** One offered house transfers to a short player, paid in room tea first. */
function transferDaaluuHouse(d: DaaluuState, debtor: number, creditor: number, pileId: string) {
  const index = d.capturePiles[creditor].findIndex(pile => pile.tiles[0]?.id === pileId);
  if (index < 0) throw new Error('Seller has no Daaluu house to transfer.');
  const [pile] = d.capturePiles[creditor].splice(index, 1);
  const tileIds = new Set(pile.tiles.map(tile => tile.id));
  d.captured[creditor] = d.captured[creditor].filter(tile => !tileIds.has(tile.id));
  d.captured[debtor].push(...pile.tiles);
  pile.slot = Math.max(-1, ...d.capturePiles[debtor].map((house, index) => house.slot ?? index)) + 1;
  d.capturePiles[debtor].push(pile);
  d.houseTransfers.push({ pileId: pile.tiles[0].id, fromSeat: creditor, toSeat: debtor });
  d.houses[creditor] = d.capturePiles[creditor].length;
  d.houses[debtor] = d.capturePiles[debtor].length;
  d.motion.movements.push({ tiles: pile.tiles, fromSeat: creditor, toSeat: debtor, from: 'capture', to: 'capture',
    phase: d.motion.movements.some(move => move.from === 'board' && move.to === 'capture') ? 2 : 0 });
  const paid = Math.min(1, d.ledger.tea[debtor]);
  d.ledger.tea[debtor] -= paid; creditDaaluuTea(d, creditor, paid);
  d.ledger.debt[debtor][creditor] += 1 - paid;
}

/** Keep all eligible sellers and buyers active until the house deficit is filled. */
function advanceDebtChoice(state: GameState) {
  const d = state.daaluu!;
  const choice = d.debtChoice!;
  if (!choice.remaining.some(count => count > 0) || !choice.surplus.some(count => count > 0)) {
    finishDaaluuSettlement(state);
    return;
  }
  d.stage = 'debt-choice'; state.turn = null;
}

function finishDaaluuSettlement(state: GameState) {
  const d = state.daaluu!, seats = d.hands.length;
  const shortOwnHouses = new Set(d.debtChoice?.shortOwnHouses ?? []);
  d.debtChoice = null;
  // Repay existing creditors from newly collected tea, without minting lives.
  for (let debtor = 0; debtor < seats; debtor++) for (let creditor = 0; creditor < seats; creditor++) {
    const paid = Math.min(d.ledger.debt[debtor][creditor], d.ledger.tea[debtor]);
    d.ledger.tea[debtor] -= paid; creditDaaluuTea(d, creditor, paid); d.ledger.debt[debtor][creditor] -= paid;
  }
  for (let debtor = 0; debtor < seats; debtor++) {
    if (d.ledger.masters[debtor] !== null || !shortOwnHouses.has(debtor)) continue;
    const creditor = d.ledger.debt[debtor].findIndex((debt, seat) => debt >= 4 && d.ledger.masters[seat] === null && seat !== debtor);
    // A slave holds no debt: enslavement erases the row instead of keeping it.
    if (creditor >= 0) makeDaaluuSlave(d, debtor, creditor);
  }
  const freedomRequirements = daaluuFreedomRequirements(d);
  d.ledger.masters.forEach((master, slave) => {
    if (master !== null && d.earnedHouses[slave] >= freedomRequirements[slave]) d.ledger.masters[slave] = null;
  });
  d.ledger.slaveOrder = d.ledger.slaveOrder.filter(slave => d.ledger.masters[slave] !== null);
  if (!checkDaaluuWinner(state)) prepareDaaluuBailout(state);
}

export function daaluuViewFor(state: GameState, seat: number, extras: ViewExtras): GameView {
  const d = state.daaluu!;
  // Each viewer may inspect their own collected tiles while the remaining stacks are dealt.
  const reveal = ['distributing', 'arranging', 'pair-call', 'pair-response', 'bailout', 'bailout-choice', 'tribute-give', 'tribute-return', 'tribute-relay-reveal', 'tribute-reveal', 'aravgui-call', 'aravgui-response', 'debt-choice', 'playing', 'round-over', 'finished'].includes(d.stage);
  return { roomId: extras.roomId, gameType: 'daaluu', phase: state.phase, format: { ...state.format },
    myPlayerId: state.playerIds[seat], mySeat: seat, myHand: [],
    players: extras.players.map((player, i) => ({ ...player, cardCount: d.hands[i].length })),
    activePlayerId: state.turn === null ? null : state.playerIds[state.turn], timeoutEndsAt: extras.timeoutEndsAt,
    turnDurationMs: extras.turnDurationMs, pause: extras.pause, trick: null, lastActions: {}, mustInclude: null,
    winnerId: state.winner === null ? null : state.playerIds[state.winner], penalties: state.winner === null ? null : Object.fromEntries(state.playerIds.map(id => [id, 0])),
    totals: Object.fromEntries(state.playerIds.map((id, i) => [id, d.ledger.tea[i]])),
    daaluu: { stage: d.stage, round: d.round, callerSeat: d.callerSeat, janlii: d.janlii, pairCollection: d.pairCollection ? { ...d.pairCollection } : null,
      tribute: structuredClone(d.tribute), tributeBatch: structuredClone(d.tributeBatch), aravgui: structuredClone(d.aravgui), motion: structuredClone(d.motion),
      shuffle: daaluuShuffleViewFor(d.shuffle), hand: reveal ? d.hands[seat].map(tile => ({ ...tile })) : [],
      trick: structuredClone(d.trick), lastTrick: structuredClone(d.lastTrick), houses: [...d.houses], earnedHouses: [...d.earnedHouses], quota: d.quota, requiredHouses: daaluuRequiredHouses(d), freedomHouses: DAALUU_FREEDOM_HOUSES, freedomHousesBySeat: daaluuFreedomRequirements(d),
      ledger: structuredClone(d.ledger), eligibleDebtors: eligibleDaaluuDebtors(d), bailoutSeats: [...d.bailoutSeats],
      bailoutBuyers: d.ledger.tea.map((_, debtor) => bailoutCandidateBuyers(d, debtor)),
      processedBailoutSeats: [...d.processedBailoutSeats], bailoutOffers: structuredClone(d.bailoutOffers.filter(offer => fundableDaaluuOffer(d, offer))),
      bailoutLoans: structuredClone(d.bailoutLoans), deadTileIds: [...d.dead[seat]], winningSeat: d.winningSeat,
      debtChoice: d.stage === 'debt-choice' && d.debtChoice ? { remaining: [...d.debtChoice.remaining], surplus: [...d.debtChoice.surplus] } : null,
      capturePiles: structuredClone(d.capturePiles), houseTransfers: [...d.houseTransfers], lowTrick: d.lowTrick, followColor: d.followColor ?? true, leaderSkips: d.leaderSkips ? { serial: d.leaderSkips.serial, seats: [...d.leaderSkips.seats] } : null,
      distribution: structuredClone(d.distribution) },
  };
}

/** Bots and timeout moves use exactly the human's projected information. */
/**
 * Medium/Hard lead for a fresh trick, from the legal leads:
 * 1. an unbeatable lead (four-tile top combination, then Daaluu + Uuluu);
 * 2. the strongest ordinary pair of rank 8+ (wins two houses; only a higher same-color or Janlii pair beats it);
 * 3. the weakest single (a weak pair would usually hand an opponent two houses);
 * 4. anything else, cheapest first, so Janlii is led only when nothing else is legal.
 */
function chooseDaaluuLead(d: { janlii: string | null }, plays: DaaluuTile[][]): DaaluuTile[] {
  const hasJanlii = (play: DaaluuTile[]) => play.some(tile => tile.kindId === d.janlii);
  const rank = (tile: DaaluuTile) => daaluuKind(tile).rank;
  const unbeatable = plays.filter(play => isTopDoublePair(play) || isDaaluuUuluuLead(play)).sort((a, b) => b.length - a.length);
  if (unbeatable.length) return unbeatable[0];
  const strongPairs = plays.filter(play => isPair(play) && !hasJanlii(play) && rank(play[0]) >= 8).sort((a, b) => rank(b[0]) - rank(a[0]));
  if (strongPairs.length) return strongPairs[0];
  const singles = plays.filter(play => play.length === 1 && !hasJanlii(play)).sort((a, b) => power(d, a[0]) - power(d, b[0]));
  if (singles.length) return singles[0];
  const cost = (play: DaaluuTile[]) => play.reduce((total, tile) => total + power(d, tile), 0);
  return [...plays].sort((a, b) => cost(a) - cost(b))[0];
}

export function chooseDaaluuAction(view: GameView, rng: Rng, difficulty: BotDifficulty = 'medium'): DaaluuAction | null {
  const d = view.daaluu;
  if (!d) return null;
  if (d.stage === 'calling') {
    const choices = DAALUU_KINDS.filter(kind => kind.copies === 2);
    const sample = rng();
    if (!Number.isFinite(sample) || sample < 0 || sample >= 1) throw new Error('Invalid injected RNG.');
    return { type: 'call', kindId: choices[Math.floor(sample * choices.length)].id };
  }
  if (d.stage === 'selecting' && d.shuffle.stacks.length) return { type: 'select', stackHandle: d.shuffle.stacks[0].handle };
  if (d.stage === 'distributing') return { type: 'collect', stackHandle: d.shuffle.stacks[(d.distribution.startIndex! + d.distribution.collected) % d.shuffle.stacks.length].handle };
  if (d.stage === 'bailout') {
    const loan = d.bailoutLoans.find(item => item.lenderSeat === view.mySeat);
    if (loan) return { type: 'bailout-loan', buyerSeat: loan.buyerSeat, accept: false };
    return d.bailoutSeats.includes(view.mySeat) && !d.processedBailoutSeats.includes(view.mySeat) &&
      !d.bailoutLoans.some(item => item.buyerSeat === view.mySeat) ? { type: 'bailout', debtorSeat: null } : null;
  }
  if (d.stage === 'bailout-choice') {
    const buyerSeat = d.bailoutOffers.find(offer => offer.debtorSeat === view.mySeat)?.buyerSeat;
    return buyerSeat === undefined ? null : { type: 'bailout-choose', buyerSeat };
  }
  if (d.stage === 'debt-choice' && d.debtChoice && d.debtChoice.surplus[view.mySeat] > 0) {
    const debtorSeat = d.debtChoice.remaining.findIndex(count => count > 0);
    const pileId = d.capturePiles[view.mySeat].at(-1)?.tiles[0]?.id;
    if (debtorSeat >= 0 && pileId) return { type: 'debt-choice', debtorSeat, pileId };
  }
  if (d.stage === 'pair-call') return { type: 'pair-call', color: 'red' };
  if (d.stage === 'tribute-give') {
    const relay = !!d.tribute?.relay || d.tributeBatch?.phase === 'relay-give';
    const required = d.tributeBatch?.phase === 'relay-give' ? daaluuBatchRelayTiles(d.hand, d.janlii)
      : daaluuRequiredTributeTiles(d.hand, d.janlii, relay);
    return { type: 'tribute', tileIds: required.map(tile => tile.id) };
  }
  if (d.stage === 'tribute-return' && d.tribute) {
    const batch = d.tributeBatch;
    const returningRelay = !!d.tribute.relay?.offered.length && !d.tribute.relay.returned.length || batch?.phase === 'relay-return';
    const returnCount = batch?.phase === 'master-return'
      ? batch.offers.reduce((count, offer) => count + offer.big.length, 0)
      : batch?.phase === 'relay-return'
        ? batch.offers[batch.relayIndex]?.middle.length ?? 0
        : returningRelay ? d.tribute.relay!.offered.length : d.tribute.offered.length;
    const tiles = [...d.hand].sort((a, b) => power(d, a) - power(d, b)).slice(0, returnCount);
    if (batch?.phase === 'master-return') {
      let offset = 0;
      const returnsBySeat = batch.offers.filter(offer => offer.big.length > 0).map(offer => {
        const row = tiles.slice(offset, offset + offer.big.length); offset += offer.big.length;
        return { slaveSeat: offer.slaveSeat, tileIds: row.map(tile => tile.id) };
      });
      return { type: 'tribute', tileIds: tiles.map(tile => tile.id), returnsBySeat };
    }
    return { type: 'tribute', tileIds: tiles.map(tile => tile.id) };
  }
  if (d.stage === 'aravgui-call') return { type: 'aravgui', tileIds: d.hand.slice(0, 2).map(tile => tile.id) };
  if (d.stage === 'aravgui-response') {
    const tiles = legalDaaluuAravguiContributions(d.hand, d.janlii)[0];
    return tiles ? { type: 'aravgui', tileIds: tiles.map(tile => tile.id) } : null;
  }
  if (d.stage === 'pair-response' && d.pairCollection) {
    const tiles = legalDaaluuPairContributions(d.hand, d.pairCollection.color)[0];
    return tiles ? { type: 'play', tileIds: tiles.map(tile => tile.id) } : null;
  }
  if (d.stage !== 'playing') return null;
  const plays = legalDaaluuHandPlays({ hand: d.hand, janlii: d.janlii, trick: d.trick,
    winningSeat: d.winningSeat, deadTileIds: d.deadTileIds, lowTrick: d.lowTrick, capturePiles: d.capturePiles, followColor: d.followColor,
  });
  const incumbent = d.trick.find(play => play.seat === d.winningSeat)?.tiles;
  if (!incumbent && difficulty !== 'easy' && plays.length) {
    const tileIds = chooseDaaluuLead(d, plays).map(tile => tile.id);
    return { type: 'play', tileIds };
  }
  // A reply that does not take the trick must never give away Janlii when another legal reply exists.
  const wastesJanlii = (play: DaaluuTile[]) => !!incumbent && play.some(tile => tile.kindId === d.janlii) && !daaluuBeats(d, play, incumbent);
  // Replies are judged by every tile they give up, not only the first (Janlii counts as 100).
  const cost = (play: DaaluuTile[]) => play.reduce((total, tile) => total + power(d, tile), 0);
  plays.sort((a, b) => {
    const waste = Number(wastesJanlii(a)) - Number(wastesJanlii(b));
    if (waste) return waste;
    if (difficulty !== 'easy' && incumbent) {
      const advantage = Number(daaluuBeats(d, b, incumbent)) - Number(daaluuBeats(d, a, incumbent));
      if (advantage) return advantage;
    }
    if (difficulty === 'easy') return power(d, b[0]) - power(d, a[0]);
    return cost(a) - cost(b) || a.length - b.length;
  });
  return plays.length ? { type: 'play', tileIds: plays[0].map(tile => tile.id) } : null;
}
